import React from 'react';
import { Lock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useThemeStore } from '../../lib/themeStore';

export type ModeAccessVariant = 'dynasty-only' | 'standard-only' | 'build-phase';

interface ModeAccessMessageProps {
  variant: ModeAccessVariant;
  /** Optional custom title */
  title?: string;
  /** Optional custom body text */
  message?: string;
  /** Optional hint showing which mode has this feature */
  modeHint?: string;
  /** If provided, shows a navigation button */
  actionLabel?: string;
  actionPath?: string;
}

/**
 * Affiche un message informatif lorsqu'une fonctionnalité n'est pas
 * disponible dans le mode actuel. Remplace le contenu de la page.
 */
export function ModeAccessMessage({
  variant,
  title,
  message,
  modeHint,
  actionLabel,
  actionPath,
}: ModeAccessMessageProps) {
  const navigate = useNavigate();
  const { theme } = useThemeStore();

  const defaults: Record<ModeAccessVariant, { icon: string; color: string; defaultTitle: string; defaultMessage: string; defaultHint: string }> = {
    'dynasty-only': {
      icon: '⚙️',
      color: 'primary',
      defaultTitle: 'Fonctionnalité Dynasty / Keeper',
      defaultMessage:
        "Cette fonctionnalité est disponible exclusivement dans le mode Dynasty/Keeper. " +
        "Ce mode propose les échanges, le repêchage, la liste de protection et la logique keeper.",
      defaultHint: 'Mode Dynasty / Keeper',
    },
    'standard-only': {
      icon: '🏗️',
      color: 'success',
      defaultTitle: 'Fonctionnalité Standard Flexible',
      defaultMessage:
        "Cette fonctionnalité est disponible exclusivement dans le mode Standard Flexible.",
      defaultHint: 'Mode Standard Flexible',
    },
    'build-phase': {
      icon: '🔒',
      color: 'warning',
      defaultTitle: 'Phase de construction en cours',
      defaultMessage:
        "La phase de construction de votre équipe est en cours. " +
        "Veuillez utiliser « Bâtir mon équipe » pour ajouter des joueurs gratuitement. " +
        "Les agents libres réguliers seront accessibles une fois votre équipe complète.",
      defaultHint: 'Accès disponible après la construction',
    },
  };

  const cfg = defaults[variant];
  const resolvedTitle = title ?? cfg.defaultTitle;
  const resolvedMessage = message ?? cfg.defaultMessage;
  const resolvedHint = modeHint ?? cfg.defaultHint;

  const cardBg = theme === 'dark' ? 'rgba(30,41,59,0.8)' : '#fff';
  const borderColor = theme === 'dark' ? 'rgba(59,130,246,0.2)' : '#dee2e6';

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
      <div
        className="card border-0 shadow-sm text-center p-4 p-md-5"
        style={{
          maxWidth: 540,
          width: '100%',
          background: cardBg,
          border: `1px solid ${borderColor}`,
        }}
      >
        {/* Icon */}
        <div style={{ fontSize: '3rem', lineHeight: 1 }} className="mb-3">
          {cfg.icon}
        </div>

        {/* Lock badge */}
        <div className={`d-inline-flex align-items-center justify-content-center mx-auto mb-3`}>
          <span className={`badge bg-${cfg.color}${cfg.color === 'warning' ? ' text-dark' : ''} d-flex align-items-center gap-1 px-3 py-2`}>
            <Lock size={13} />
            {resolvedHint}
          </span>
        </div>

        {/* Title */}
        <h5 className="fw-bold mb-2">{resolvedTitle}</h5>

        {/* Message */}
        <p className="text-muted mb-4" style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
          {resolvedMessage}
        </p>

        {/* Optional action button */}
        {actionLabel && actionPath && (
          <button
            className={`btn btn-${cfg.color}${cfg.color === 'warning' ? ' text-dark' : ''} d-inline-flex align-items-center gap-2 mx-auto`}
            onClick={() => navigate(actionPath)}
          >
            {actionLabel}
            <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
